import React, {createContext, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {actions} from 'pages/Chart/Chart.redux';

export type ChartingCodeOptions = {
	canvasDown: [ number, number ] | null,
	canvasUp: [ number, number ] | null
	canvas: string | null
	layer: string | null
	sourceType: 'tooth' | 'mouth' | null
	symbolID: string | number | null
	tooth: string | null
}

export type ChartingCodeContextType = {
	tab: number | null
	values: Record<string, any>
	setTab: (tab: number | null) => void
	setValues: (values: Record<string, any>) => void
	setValue: (key: string, value: any) => void
	handleSave: () => void
	layout: Array<any>
	requiredValuesFilled: boolean
	options: ChartingCodeOptions
}

const defaultContext: ChartingCodeContextType = {
	tab: null,
	values: {},
	setTab: () => null,
	setValues: () => null,
	setValue: () => null,
	handleSave: () => null,
	layout: [],
	requiredValuesFilled: false,
	options: {
		canvasDown: null,
		canvasUp: null,
		canvas: null,
		layer: null,
		sourceType: null,
		symbolID: null,
		tooth: null
	}
};


export const ChartingCodeContext = createContext(defaultContext);


function generateLayoutValues(codeConfig) {
	const hasTabs = Boolean(codeConfig.tabs);
	const newTab = hasTabs
				   ? 0
				   : null;
	const layout = hasTabs
				   ? codeConfig.tabs[newTab].layout
				   : codeConfig.layout;

	if (!hasTabs || !codeConfig.saveAllTabValues) {
		return layout.reduce((accumulator, current) => {
			accumulator[current.valueName] = current.defaultValue;
			return accumulator;
		}, {});
	}

	// All tab values
	return codeConfig.tabs.reduce((accumulator, current) => {
		current.layout.forEach(tabLayout => {
			accumulator[tabLayout.valueName] = tabLayout.defaultValue;
		});

		return accumulator;
	}, {});
}

function checkRequiredValuesFilled(layout, values) {
	return layout.every(item => {
		if (item.required) {
			const value = values[item.valueName];
			return value !== undefined && value !== null && value !== '' && value !== false;
		}

		return true;
	});
}

export const ChartingCodeProvider = ({children, codeConfig}) => {
	const dispatch = useDispatch();
	const [ tab, setTab ] = useState<number | null>(null);
	const [ layout, setLayout ] = useState([]);
	const [ values, setValues ] = useState<Record<string, any>>({});
	const [ requiredValuesFilled, setRequiredValuesFilled ] = useState<boolean>(false);
	const optionsRaw = useSelector(state => state.chart.symbolOptions);
	const symbolID = optionsRaw.symbol;

	const options = useMemo(() => {
		return {
			canvasDown: optionsRaw?.canvasDown || null,
			canvasUp: optionsRaw?.canvasUp || null,
			canvas: optionsRaw?.canvas || null,
			layer: optionsRaw?.layer || null,
			sourceType: optionsRaw?.sourceType || null,
			symbolID: optionsRaw?.symbol || null,
			tooth: optionsRaw?.tooth || null
		};
	}, [ optionsRaw ]);


	const setValue = useCallback((key, value) => {
		setValues((currentValues) => ({...currentValues, [key]: value}));
	}, [ setValues ]);

	const handleSave = useCallback(() => {
		const data = {
			values,
			tab
		};

		dispatch(actions.saveSymbolV2({data, layout, options, symbolID, codeConfig}));
	}, [ dispatch, values, tab, layout, options, symbolID, codeConfig ]);


	const providerValue = useMemo(() => {
		return {
			tab,
			setTab,
			values,
			setValues,
			setValue,
			layout,
			requiredValuesFilled,
			handleSave,
			options: optionsRaw
		};
	}, [ tab, setTab, values, setValues, setValue, layout, requiredValuesFilled, optionsRaw ]);


	useEffect(() => {
		if (!codeConfig) {
			setTab(null);
			setValues({});
		} else {
			const hasTabs = Boolean(codeConfig.tabs);
			const newTab = hasTabs
						   ? 0
						   : null;
			const layout = hasTabs
						   ? codeConfig.tabs[newTab].layout
						   : codeConfig.layout;
			const newValues = generateLayoutValues(codeConfig);

			setTab(newTab);
			setValues(newValues);
			setLayout(layout);
		}
	}, [ codeConfig, setTab, setValues, setLayout ]);


	// Update values and layout when changing tabs
	useEffect(() => {
		if (tab !== null && codeConfig.tabs?.[tab]) {
			const layout = codeConfig.tabs[tab].layout;

			if (!codeConfig.saveAllTabValues) {
				const newValues = layout.reduce((accumulator, current) => {
					accumulator[current.valueName] = current.defaultValue;
					return accumulator;
				}, {});

				setValues(newValues);
			}

			setLayout(layout);
		}
	}, [ tab, codeConfig, setLayout, setValues ]);


	// Update whether required values are filled
	useEffect(() => {
		let allRequiredFilled: boolean = false;

		const anyValuesFilled = Object.keys(values).some((key) => {
			const value = values[key];
			return value !== undefined && value !== null && value !== '' && value !== false;
		});

		if (codeConfig.tabs && codeConfig.saveAllTabValues) {
			allRequiredFilled = codeConfig.tabs.every(tab => checkRequiredValuesFilled(tab.layout, values));
		} else if (codeConfig.tabs && codeConfig.tabs?.[tab]) {
			allRequiredFilled = checkRequiredValuesFilled(codeConfig.tabs[tab].layout, values);
		} else {
			allRequiredFilled = checkRequiredValuesFilled(codeConfig.layout, values);
		}

		setRequiredValuesFilled(allRequiredFilled && anyValuesFilled);
	}, [ tab, layout, values, setRequiredValuesFilled ]);

	return (
		<ChartingCodeContext.Provider value={providerValue}>
			{children}
		</ChartingCodeContext.Provider>
	);
};

